import Card from './../../components/common/Card';
import Label from '../../components/common/Label';
import { useTranslation } from 'react-i18next';
import SelectSearch from '../../components/common/SelectSearch';
import ReactCalender from '../../components/common/Datepicker';
import '../../assets/css/pages/stores/export-summary.css';
import { useEffect, useMemo, useState } from 'react';
import { formattedDate, maxEndDateMin, maxStartDateMin, minRequestedDateMin, minStartDateMin } from '../../utils/helpers/date';
import env from '../../configs/env';
import { createCompetitorChainOptions, createCompetitorStoreMultiOptions, createDepartmentOptions } from '../../utils/helpers/option';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCompetitorChainsByStoreCode, fetchCompetitorStoresByStoreCode, fetchDepartments } from '../../redux/actions/api';
import MultiSelect from '../../components/common/MultiSelect';
import { setToastsError } from '../../redux/actions/common';
import { ExportControllerService } from '../../services';
import apiErrorHandler from '../../api/apiErrorHandler';
import { clearErrors } from '../../redux/actions/error';
import { EitherOneRequiredText } from '../../components/common/RequiredBadge';

const DEFAULT_STATE = {
    selectedDepartment: null,
    competitorChainCode: [],
    competitorStoreCode: [],
    competitorChainCodes: [],
    competitorStoreCodes: [],
    requestedDateMin: '',
    requestedDateMax: '',
    startDateMin: '',
    startDateMax: '',
    endDateMin: '',
    endDateMax: '',
};

const ExportSummary = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const constants = useSelector((state) => state.constants);
    const { departments, competitorChains, competitorStores } = constants;

    const [state, setState] = useState(DEFAULT_STATE);

    const {
        selectedDepartment,
        competitorChainCode,
        competitorStoreCode,
        competitorChainCodes,
        competitorStoreCodes,
        requestedDateMin,
        requestedDateMax,
        startDateMin,
        startDateMax,
        endDateMin,
        endDateMax,
    } = state;

    useEffect(() => {
        dispatch(clearErrors());
        dispatch(fetchDepartments());
        dispatch(fetchCompetitorChainsByStoreCode());
        dispatch(fetchCompetitorStoresByStoreCode());
    }, [dispatch]);

    // カレンダー制御範囲
    const {
        // 入力日
        requestedDateMinMinDate,
        requestedDateMinMaxDate,
        requestedDateMaxMinDate,
        requestedDateMaxMaxDate,
        // 期間開始
        startDateMinMinDate,
        startDateMinMaxDate,
        startDateMaxMinDate,
        startDateMaxMaxDate,
        // 期間終了
        endDateMinMinDate,
        endDateMinMaxDate,
        endDateMaxMinDate,
        endDateMaxMaxDate,
    } = useMemo(() => {
        const now = new Date();
        const minRequestedDate = minRequestedDateMin();
        const minStartDate = minStartDateMin();
        const maxStartDate = maxStartDateMin();

        return {
            requestedDateMinMinDate: minRequestedDate,
            requestedDateMinMaxDate: now,

            requestedDateMaxMinDate: requestedDateMin ? new Date(requestedDateMin) : minRequestedDate,
            requestedDateMaxMaxDate: now,

            startDateMinMinDate: minStartDate,
            startDateMinMaxDate: maxStartDate,

            startDateMaxMinDate: startDateMin ? new Date(startDateMin) : minStartDate,
            startDateMaxMaxDate: maxStartDate,

            endDateMinMinDate: startDateMin ? new Date(startDateMin) : minRequestedDate,
            endDateMinMaxDate: maxEndDateMin(startDateMin),

            endDateMaxMinDate: endDateMin ? new Date(endDateMin) : startDateMin ? new Date(startDateMin) : minRequestedDate,
            endDateMaxMaxDate: maxEndDateMin(startDateMax),
        };
    }, [requestedDateMin, startDateMin, startDateMax, endDateMin]);

    const handleChangeState = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleChangeMultiState = (field, option = []) => {
        setState((prevState) => ({
            ...prevState,
            [field]: option,
        }));
    };

    const handleChainChange = (option, values) => {
        const newCompetitorStoreCode = competitorStoreCode?.filter((store) => values.some((value) => store.value.startsWith(value)));

        const newCompetitorStoreCodes = competitorStoreCodes?.filter((store) => values.some((value) => store.startsWith(value)));

        handleChangeMultiState('competitorStoreCode', newCompetitorStoreCode);
        handleChangeMultiState('competitorStoreCodes', newCompetitorStoreCodes);
    };

    const filteredChildOptions = competitorChainCode ? createCompetitorStoreMultiOptions(competitorStores, competitorChainCodes) : [];

    const handleClearForm = () => {
        setState((prevState) => ({
            ...prevState,
            requestedDateMin: '',
            requestedDateMax: '',
            selectedDepartment: null,
            competitorChainCode: [],
            competitorStoreCode: [],
            competitorChainCodes: [],
            competitorStoreCodes: [],
            startDateMin: '',
            startDateMax: '',
            endDateMin: '',
            endDateMax: '',
        }));
    };

    const isChecked = () => {
        const values = [
            selectedDepartment?.value ?? '',
            requestedDateMin,
            requestedDateMax,
            startDateMin,
            startDateMax,
            endDateMin,
            endDateMax,
        ];
        const arrays = [competitorChainCode, competitorStoreCode, competitorChainCodes, competitorStoreCodes];

        return !(values.some((value) => value !== '' && value !== null) || arrays.some((value) => value.length !== 0));
    };

    // 入力された日付がカレンダーの制御範囲外の場合エラートーストを表示する
    const validateDate = () => {
        // 入力日: 開始日
        if (requestedDateMin) {
            if (requestedDateMin < formattedDate(requestedDateMinMinDate)) {
                dispatch(setToastsError('入力日の開始日は3ヶ月前以降の日付を選択してください。'));
                return false;
            } else if (requestedDateMin > formattedDate(requestedDateMinMaxDate)) {
                dispatch(setToastsError('入力日の開始日は本日までの日付を選択してください。'));
                return false;
            }
        }

        // 入力日: 終了日
        if (requestedDateMax) {
            if (requestedDateMax < formattedDate(requestedDateMaxMinDate)) {
                if (requestedDateMin) {
                    // 入力日の開始日入力時
                    dispatch(setToastsError('入力日の終了日は開始日以降の日付を選択してください。'));
                    return false;
                } else {
                    dispatch(setToastsError('入力日の終了日は3ヶ月前以降の日付を選択してください。'));
                    return false;
                }
            } else if (requestedDateMax > formattedDate(requestedDateMaxMaxDate)) {
                dispatch(setToastsError('入力日の終了日は本日までの日付を選択してください。'));
                return false;
            }
        }

        // 期間開始: 開始日
        if (startDateMin) {
            if (startDateMin < formattedDate(startDateMinMinDate)) {
                dispatch(setToastsError('期間開始の開始日は2週間前以降の日付を選択してください。'));
                return false;
            } else if (startDateMin > formattedDate(startDateMinMaxDate)) {
                dispatch(setToastsError('期間開始の開始日は2週間後までの日付を選択してください。'));
                return false;
            }
        }

        // 期間開始: 終了日
        if (startDateMax) {
            if (startDateMax < formattedDate(startDateMaxMinDate)) {
                if (startDateMin) {
                    // 期間開始の開始日入力時
                    dispatch(setToastsError('期間開始の終了日は開始日以降の日付を選択してください。'));
                    return false;
                } else {
                    dispatch(setToastsError('期間開始の終了日は2週間前以降の日付を選択してください。'));
                    return false;
                }
            } else if (startDateMax > formattedDate(startDateMaxMaxDate)) {
                dispatch(setToastsError('期間開始の終了日は2週間後までの日付を選択してください。'));
                return false;
            }
        }

        // 期間終了: 開始日
        if (endDateMin) {
            if (endDateMin < formattedDate(endDateMinMinDate)) {
                if (startDateMin) {
                    // 期間開始の開始日入力時
                    dispatch(setToastsError('期間終了の開始日は期間開始の開始日以降の日付を選択してください。'));
                    return false;
                } else {
                    dispatch(setToastsError('期間終了の開始日は3ヶ月前以降の日付を選択してください。'));
                    return false;
                }
            } else if (endDateMin > formattedDate(endDateMinMaxDate)) {
                if (startDateMin) {
                    // 期間開始の開始日入力時
                    dispatch(setToastsError('期間終了の開始日は期間開始の開始日から30日後までの日付を選択してください。'));
                    return false;
                } else {
                    dispatch(setToastsError('期間終了の開始日は本日から30日後までの日付を選択してください。'));
                    return false;
                }
            }
        }

        // 期間終了: 終了日
        if (endDateMax) {
            if (endDateMax < formattedDate(endDateMaxMinDate)) {
                if (endDateMin) {
                    // 期間終了の開始日入力時
                    dispatch(setToastsError('期間終了の終了日は期間終了の開始日以降を選択してください。'));
                    return false;
                } else if (startDateMin) {
                    // 期間終了の開始日未入力 かつ 期間開始の開始日入力時
                    dispatch(setToastsError('期間終了の終了日は期間開始の開始日以降を選択してください。'));
                    return false;
                } else {
                    dispatch(setToastsError('期間終了の終了日は3ヶ月前以降の日付を選択してください。'));
                    return false;
                }
            } else if (endDateMax > formattedDate(endDateMaxMaxDate)) {
                if (startDateMax) {
                    // 期間開始の終了日入力時
                    dispatch(setToastsError('期間終了の終了日は期間開始の終了日から30日後までの日付を選択してください。'));
                    return false;
                } else {
                    dispatch(setToastsError('期間終了の終了日は本日から30日後までの日付を選択してください。'));
                    return false;
                }
            }
        }
        return true;
    };

    const handleSubmitForm = () => {
        dispatch(clearErrors());
        setState((prevState) => ({
            ...prevState,
            ErrorRequestedDateMin: false,
            ErrorRequestedDateMax: false,
        }));
        if (isChecked()) {
            dispatch(setToastsError(t('export_summary.error-message')));
        } else if (!validateDate()) {
            return;
        } else {
            handleExportFile();
        }
    };

    const handleExportFile = () => {
        const params = {
            departmentCode: selectedDepartment?.value ?? null,
            competitorChainCodes,
            competitorStoreCodes,
            requestedDateMin,
            requestedDateMax,
            startDateMin,
            startDateMax,
            endDateMin,
            endDateMax,
        };

        const filteredParams = {};
        for (const key in params) {
            if (!(params[key] === '' || params[key] === null)) {
                filteredParams[key] = params[key];
            }
        }

        // バックエンドからダウンロードするため、formを作成してリクエスト
        const form = document.createElement('form');
        form.action = `${env.BACKEND_BASE_URL}/api/export-summary`;
        form.method = 'POST';
        form.addEventListener('formdata', (e) => {
            const fd = e.formData;
            for (const key in filteredParams) {
                fd.append(key, filteredParams[key]);
            }
        });
        document.body.append(form);

        ExportControllerService.validateExportSummary(filteredParams)
            .then(() => {
                form.submit();
                handleClearForm();
            })
            .catch((error) => {
                apiErrorHandler(dispatch, error);
            })
            .finally(() => {
                form.remove();
            });
    };

    return (
        <div className="export-summary">
            <div className="okc-inner">
                <Card handleSubmit={handleSubmitForm} handleClear={handleClearForm} isExport={true} toggle={false}>
                    <Label
                        className="col-lg-2 mb-2 mb-lg-4"
                        type="column"
                        text={t('export_summary.input_date.title')}
                        isEitherOneRequired={true}
                    />
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            classNameInputDateTime="js-datepicker requestedDateMin"
                            onChange={(value) => handleChangeState('requestedDateMin', value)}
                            value={requestedDateMin}
                            minDate={requestedDateMinMinDate}
                            maxDate={requestedDateMinMaxDate}
                        />
                    </div>
                    <div className="col-lg-2 justify-content-center mb-4">〜</div>
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            classNameInputDateTime="js-datepicker requestedDateMax"
                            onChange={(value) => handleChangeState('requestedDateMax', value)}
                            value={requestedDateMax}
                            minDate={requestedDateMaxMinDate}
                            maxDate={requestedDateMaxMaxDate}
                        />
                    </div>
                    <div className="row no-gutters w-100 js-okc-validate">
                        <div className="col-lg-2"></div>
                        <div className="col-lg-2"></div>
                    </div>
                    <Label
                        className="col-lg-2 mb-2 mb-lg-4"
                        type="column"
                        text={t('export_summary.division.title')}
                        style={{ height: '40px' }}
                        isEitherOneRequired={true}
                    />
                    <div className="col-lg-4 mb-4">
                        <SelectSearch
                            isClearable={true}
                            placeholder={t('export_summary.division_name.placeholder')}
                            isSearch={false}
                            onChange={(value) => handleChangeState('selectedDepartment', value)}
                            options={createDepartmentOptions(departments)}
                            value={selectedDepartment?.value}
                        />
                    </div>
                    <div className="col-lg-6"></div>
                    <Label
                        className="col-lg-2 mb-2 mb-lg-4"
                        type="column"
                        text={t('export_summary.competitive_chain.title')}
                        isEitherOneRequired={true}
                    />
                    <div className="col-lg-4 mb-4">
                        <MultiSelect
                            placeholder={t('export_summary.competitive_chain.placeholder')}
                            onChange={(option, codes) => {
                                handleChangeMultiState('competitorChainCode', option);
                                handleChangeMultiState('competitorChainCodes', codes);
                                handleChainChange(option, codes);
                            }}
                            options={createCompetitorChainOptions(competitorChains)}
                            value={competitorChainCode}
                        />
                    </div>
                    <Label
                        className="col-lg-2 mb-2 mb-lg-4 text-center competitor-store-name"
                        type="column"
                        text={t('export_summary.competitive_store.title')}
                        style={{ justifyContent: 'center' }}
                        isEitherOneRequired={true}
                    />
                    <div className="col-lg-4 mb-4">
                        <MultiSelect
                            placeholder={t('export_summary.competitive_store.placeholder')}
                            onChange={(option, codes) => {
                                handleChangeMultiState('competitorStoreCode', option);
                                handleChangeMultiState('competitorStoreCodes', codes);
                            }}
                            options={filteredChildOptions}
                            value={competitorStoreCode}
                            disabled={!competitorChainCode.length}
                        />
                    </div>
                    <Label
                        className="col-lg-2 mb-2 mb-lg-4"
                        type="column"
                        text={t('export_summary.period_start.title')}
                        isEitherOneRequired={true}
                    />
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            classNameInputDateTime="js-datepicker startDateMin"
                            onChange={(value) => handleChangeState('startDateMin', value)}
                            value={startDateMin}
                            minDate={startDateMinMinDate}
                            maxDate={startDateMinMaxDate}
                        />
                    </div>
                    <div className="col-lg-2 justify-content-center mb-4">〜</div>
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            classNameInputDateTime="js-datepicker startDateMax"
                            onChange={(value) => handleChangeState('startDateMax', value)}
                            value={startDateMax}
                            minDate={startDateMaxMinDate}
                            maxDate={startDateMaxMaxDate}
                        />
                    </div>
                    <Label
                        className="col-lg-2 mb-2 mb-lg-4"
                        type="column"
                        text={t('export_summary.period_end.title')}
                        isEitherOneRequired={true}
                    />
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            classNameInputDateTime="js-datepicker endDateMin"
                            onChange={(value) => handleChangeState('endDateMin', value)}
                            value={endDateMin}
                            minDate={endDateMinMinDate}
                            maxDate={endDateMinMaxDate}
                        />
                    </div>
                    <div className="col-lg-2 justify-content-center mb-4">〜</div>
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            classNameInputDateTime="js-datepicker endDateMax"
                            onChange={(value) => handleChangeState('endDateMax', value)}
                            value={endDateMax}
                            minDate={endDateMaxMinDate}
                            maxDate={endDateMaxMaxDate}
                        />
                    </div>
                    <EitherOneRequiredText />
                </Card>
            </div>
        </div>
    );
};
export default ExportSummary;
